import type { EChartsType } from 'echarts/core';
import type { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { toInteger } from 'lodash';
import { getLocaleStore } from '@/i18n';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import type { IChartLayer } from '@/version-types';
import { getChartOptionsWithRuntimeFonts, getEchartsOptions, loadEcharts } from '../utils/core';
import { checkChartIsEmpty, EMPTY_CHART_MAP } from '../utils/render';

interface InteractiveCanvasResult {
    canvas: HTMLCanvasElement;
    chartInstance: EChartsType | null;
    dispose: () => void;
}

interface InteractiveCanvasOptions {
    animation?: boolean;
    onFrame?: (canvas: HTMLCanvasElement) => void;
    onEnd?: (canvas: HTMLCanvasElement) => void;
    onTooltipChange?: (params: TopLevelFormatterParams) => void;
}

const CHART_CANVAS_ANIMATION_MAX_DURATION = 3000;

const drawRoundRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) => {
    const safeRadius = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(x + safeRadius, y);
    ctx.lineTo(x + width - safeRadius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
    ctx.lineTo(x + width, y + height - safeRadius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
    ctx.lineTo(x + safeRadius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
    ctx.lineTo(x, y + safeRadius);
    ctx.quadraticCurveTo(x, y, x + safeRadius, y);
    ctx.closePath();
};

const renderEmptyChartToCanvas = async (layer: IChartLayer): Promise<HTMLCanvasElement> => {
    const width = Math.max(1, toInteger(layer.width));
    const height = Math.max(1, toInteger(layer.height));
    const chartSize = Math.min(width, height);
    const { canvas, ctx } = createCanvas(width, height);
    const image = await loadImageAsync(EMPTY_CHART_MAP[layer.data.type], false);
    const imageX = (width - chartSize) / 2;
    const imageY = (height - chartSize) / 2;

    ctx.drawImage(image, imageX, imageY, chartSize, chartSize);

    const tipWidth = chartSize * 0.65;
    const tipHeight = chartSize * 0.2;
    const tipX = (width - tipWidth) / 2;
    const tipY = (height - tipHeight) / 2;
    const fontSize = chartSize * 0.06;
    const { $t } = getLocaleStore();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    drawRoundRect(ctx, tipX, tipY, tipWidth, tipHeight, chartSize * 0.052);
    ctx.fill();

    ctx.fillStyle = '#616366';
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
        $t((v) => v.chartLayer.emptyChart),
        width / 2,
        height / 2,
    );

    return canvas;
};

const getChartLayerData = (layer: IChartLayer) => {
    return getChartOptionsWithRuntimeFonts(layer.data, layer.__runtime?.fontFamilyMap ?? {});
};

const renderChartToCanvas = async (layer: IChartLayer, scale = 1): Promise<HTMLCanvasElement> => {
    if (checkChartIsEmpty(layer)) {
        return renderEmptyChartToCanvas(layer);
    }

    const width = Math.max(1, toInteger(layer.width));
    const height = Math.max(1, toInteger(layer.height));
    const pixelRatio = Math.max(0.01, scale);
    const { canvas } = createCanvas(width, height);
    const echarts = await loadEcharts();
    const chartInstance = echarts.init(canvas as unknown as HTMLElement, null, {
        renderer: 'canvas',
        devicePixelRatio: pixelRatio,
        width,
        height,
    });

    const echartsOptions = getEchartsOptions(getChartLayerData(layer), {
        type: layer.data.type,
        chartWidth: layer.width,
        chartHeight: layer.height,
    });

    try {
        chartInstance.setOption(
            {
                ...echartsOptions,
                animation: false,
            },
            {
                notMerge: true,
                silent: true,
                lazyUpdate: false,
            },
        );

        return chartInstance.renderToCanvas({ pixelRatio });
    } finally {
        chartInstance.dispose();
    }
};

export const renderInteractiveLayerContentToCanvas = async (
    layer: IChartLayer,
    scale = 1,
    options: InteractiveCanvasOptions = {},
): Promise<InteractiveCanvasResult> => {
    if (checkChartIsEmpty(layer)) {
        return {
            canvas: await renderEmptyChartToCanvas(layer),
            chartInstance: null,
            dispose: () => {},
        };
    }

    const width = Math.max(1, toInteger(layer.width));
    const height = Math.max(1, toInteger(layer.height));
    const pixelRatio = Math.max(0.01, scale);
    const { canvas: chartCanvas } = createCanvas(width, height);
    const echarts = await loadEcharts();
    const chartInstance = echarts.init(chartCanvas as unknown as HTMLElement, null, {
        renderer: 'canvas',
        devicePixelRatio: pixelRatio,
        width,
        height,
    });

    const echartsOptions = getEchartsOptions(getChartLayerData(layer), {
        type: layer.data.type,
        chartWidth: layer.width,
        chartHeight: layer.height,
    });

    let disposed = false;
    let animationTimer: ReturnType<typeof setTimeout> | null = null;

    function notifyFrame() {
        if (disposed) return;
        options.onFrame?.(chartCanvas);
    }

    function stopAnimationListeners() {
        chartInstance.off('finished', finishAnimation);
    }

    function finishAnimation() {
        if (disposed) return;
        notifyFrame();
        options.onEnd?.(chartCanvas);
        if (animationTimer) {
            clearTimeout(animationTimer);
            animationTimer = null;
        }
        stopAnimationListeners();
    }

    function dispose() {
        if (disposed) return;
        disposed = true;
        if (animationTimer) {
            clearTimeout(animationTimer);
            animationTimer = null;
        }
        stopAnimationListeners();
        chartInstance.off('rendered', notifyFrame);
        chartInstance.dispose();
    }

    try {
        chartInstance.on('rendered', notifyFrame);
        if (options.animation) {
            chartInstance.on('finished', finishAnimation);
        }

        chartInstance.setOption(
            {
                ...echartsOptions,
                tooltip: {
                    ...(echartsOptions.tooltip as Record<string, unknown>),
                    formatter: (params: TopLevelFormatterParams) => {
                        options.onTooltipChange?.(params);
                        return '';
                    },
                },
                animation: options.animation ?? false,
            },
            {
                notMerge: true,
                silent: true,
                lazyUpdate: false,
            },
        );

        notifyFrame();
        if (options.animation) {
            animationTimer = setTimeout(finishAnimation, CHART_CANVAS_ANIMATION_MAX_DURATION);
        }
    } catch (error) {
        dispose();
        throw error;
    }

    return {
        canvas: chartCanvas,
        chartInstance,
        dispose,
    };
};

export const renderLayerContentToCanvas = async (
    layer: IChartLayer,
    globalAlpha = false,
    scale = 1,
): Promise<HTMLCanvasElement> => {
    const layerCanvas = await renderChartToCanvas(layer, scale);
    const resultWidth = Math.max(1, toInteger(layer.width * scale));
    const resultHeight = Math.max(1, toInteger(layer.height * scale));
    const { canvas, ctx } = createCanvas(resultWidth, resultHeight);

    ctx.globalAlpha = globalAlpha ? layer.opacity : 1;
    ctx.drawImage(layerCanvas, 0, 0, resultWidth, resultHeight);

    return canvas;
};
